import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Mail, X, AlertCircle, Plus } from 'lucide-react';

interface InviteTeamStepProps {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  organizationId?: string;
}

interface TeamMember {
  email: string;
  role: 'founder' | 'employee' | 'advisor' | 'investor';
  name?: string;
}

export function InviteTeamStep({ data, onUpdate, onNext, organizationId }: InviteTeamStepProps) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(
    data.teamMembers || [{ email: '', role: 'employee' }]
  );

  const addTeamMember = () => {
    setTeamMembers([...teamMembers, { email: '', role: 'employee' }]);
  };

  const removeTeamMember = (index: number) => {
    setTeamMembers(teamMembers.filter((_, i) => i !== index));
  };

  const updateTeamMember = (index: number, field: keyof TeamMember, value: string) => {
    const updated = [...teamMembers];
    updated[index] = { ...updated[index], [field]: value };
    setTeamMembers(updated);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Filter out empty emails
      const validMembers = teamMembers.filter(member => member.email.trim());

      if (validMembers.length > 0) {
        // Create invitations for each team member
        const invitations = validMembers.map(member => ({
          organization_id: organizationId,
          email: member.email.toLowerCase(),
          role: member.role,
          invited_by: user!.id,
          metadata: {
            name: member.name,
            invited_during_onboarding: true
          }
        }));

        const { error: inviteError } = await supabase
          .from('invitations')
          .insert(invitations);

        if (inviteError) throw inviteError;

        // Update getting started checklist
        await supabase
          .from('users')
          .update({
            getting_started_checklist: {
              invited_team_member: true
            }
          })
          .eq('id', user!.id);

        // Track analytics
        await supabase
          .from('onboarding_analytics')
          .insert({
            user_id: user!.id,
            event_name: 'team_invited',
            event_data: { 
              organization_id: organizationId,
              members_invited: validMembers.length 
            }
          });

        // TODO: Send invitation emails via worker
      }

      // Update step data
      onUpdate({
        teamMembers: validMembers
      });

      onNext();
    } catch (err: any) {
      console.error('Error inviting team:', err);
      setError(err.message || 'Failed to send invitations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    onUpdate({ teamMembers: [] });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Users className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold">Invite your team</h3>
          <p className="text-sm text-muted-foreground">
            Add co-founders, employees, and advisors. You can always add more later.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        {teamMembers.map((member, index) => (
          <div key={index} className="space-y-3 p-4 border rounded-lg">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-muted-foreground mt-2" />
              <div className="flex-1 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor={`email-${index}`}>Email Address</Label>
                    <Input
                      id={`email-${index}`}
                      type="email"
                      placeholder="colleague@example.com"
                      value={member.email}
                      onChange={(e) => updateTeamMember(index, 'email', e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`role-${index}`}>Role</Label>
                    <Select
                      value={member.role}
                      onValueChange={(value) => updateTeamMember(index, 'role', value)}
                      disabled={isLoading}
                    >
                      <SelectTrigger id={`role-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="founder">Founder</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="advisor">Advisor</SelectItem>
                        <SelectItem value="investor">Investor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`name-${index}`}>Name (optional)</Label>
                  <Input
                    id={`name-${index}`}
                    placeholder="John Doe"
                    value={member.name || ''}
                    onChange={(e) => updateTeamMember(index, 'name', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
              {teamMembers.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeTeamMember(index)}
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          onClick={addTeamMember}
          disabled={isLoading}
          className="w-full"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Another Team Member
        </Button>
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          variant="outline"
          onClick={handleSkip}
          disabled={isLoading}
          className="flex-1"
        >
          Skip for now
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? 'Sending Invitations...' : 'Send Invitations'}
        </Button>
      </div>
    </div>
  );
}